import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Printer } from "lucide-react";

import { HoverHintTrigger } from "@/components/ui/title-help";
import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { zplPrintStationLabel, type PublicStationOption, type ZplPrintDocument } from "@/lib/zplPrintLocation";
import { cn } from "@/lib/utils";

const INLINE_PRINTER_SELECT_MIN_WIDTH_PX = 220;

const HEADER_TRIGGER_CLASS = "min-w-[10rem] w-auto whitespace-nowrap";

/** Width of an element's contents, ignoring flex growth of the element itself. */
function elementContentWidth(element: Element): number {
  const range = element.ownerDocument.createRange();
  range.selectNodeContents(element);
  return range.getBoundingClientRect().width;
}

function gapPx(element: Element): number {
  const styles = getComputedStyle(element);
  return Number.parseFloat(styles.columnGap || styles.gap || "0") || 0;
}

/**
 * Free header space beside the printer control. Title width is the text, not a flex-1 box,
 * and the title is counted once even when the control is a direct header child.
 */
export function headerRoomForInlinePrinterSelect(header: HTMLElement, widget: HTMLElement): number {
  const headerWidth = header.getBoundingClientRect().width;
  let used = 0;
  let items = 1;
  for (const child of header.children) {
    if (child === widget) continue;
    if (child.contains(widget)) {
      let clusterUsed = 0;
      let clusterItems = 0;
      for (const nested of child.children) {
        if (nested === widget || nested.contains(widget)) continue;
        clusterUsed += elementContentWidth(nested);
        clusterItems += 1;
      }
      if (clusterItems === 0) continue;
      used += clusterUsed + gapPx(child) * Math.max(0, clusterItems - 1);
      items += 1;
      continue;
    }
    used += elementContentWidth(child);
    items += 1;
  }
  return headerWidth - used - gapPx(header) * Math.max(0, items - 1);
}

/**
 * Header location control for floor label pages. Hub resolves the printer
 * from this station plus the page's stock size and DPI.
 */
export function InboundZplPrinterHeaderButton({
  station,
  onStationChange,
  unavailableMessage,
  documents,
  onPopoverOpenChange,
  className,
}: {
  station: string;
  onStationChange: (station: string) => void;
  unavailableMessage?: string | null;
  documents: readonly ZplPrintDocument[];
  onPopoverOpenChange?: (open: boolean) => void;
  className?: string;
}): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const [inlineSelect, setInlineSelect] = useState(true);

  const handleListOpenChange = useCallback(
    (open: boolean) => {
      onPopoverOpenChange?.(open);
    },
    [onPopoverOpenChange],
  );

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (element == null) return;
    const parent = element.parentElement;
    if (parent == null) return;

    const measure = (): void => {
      const header = element.closest("header");
      const available =
        header instanceof HTMLElement
          ? headerRoomForInlinePrinterSelect(header, element)
          : parent.getBoundingClientRect().width;
      setInlineSelect(available >= INLINE_PRINTER_SELECT_MIN_WIDTH_PX);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(parent);
    const header = element.closest("header");
    if (header != null && header !== parent) observer.observe(header);
    return () => observer.disconnect();
  }, []);

  const stationsQuery = useQuery({
    queryKey: ["public-stations"],
    queryFn: () => apiFetch<{ stations: PublicStationOption[] }>("/api/zpl-printers/stations"),
    staleTime: 120_000,
  });
  const summary = zplPrintStationLabel(station, stationsQuery.data?.stations ?? []);
  const select = (
    <ZplLocationSelect
      id="inbound-header-zpl-location"
      label="Printer"
      showLabel={false}
      size="compact"
      triggerMode={inlineSelect ? "labeled" : "icon"}
      triggerIcon={<Printer className="h-5 w-5 shrink-0" aria-hidden />}
      ariaSummary={summary}
      className="min-w-0"
      triggerClassName={inlineSelect ? HEADER_TRIGGER_CLASS : undefined}
      value={station}
      onChange={onStationChange}
      onListOpenChange={handleListOpenChange}
      unavailableMessage={inlineSelect ? unavailableMessage : null}
      documents={documents}
    />
  );

  return (
    <div
      ref={containerRef}
      className={cn("flex shrink-0 justify-end", inlineSelect ? "max-w-full" : "min-w-10", className)}
    >
      {!inlineSelect ? (
        <HoverHintTrigger align="end" openOnTriggerActivate closeOnTriggerActivate trigger={select}>
          {unavailableMessage != null && unavailableMessage !== "" ? unavailableMessage : summary}
        </HoverHintTrigger>
      ) : (
        select
      )}
    </div>
  );
}
